import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  ShieldCheck,
  Cpu,
  Zap,
  Globe2,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Compass,
  Layers,
  Check,
  ArrowUpRight,
} from 'lucide-react';
import { useCms } from '../context/CmsContext';

interface AboutSectionProps {
  onOpenInquiry: (subject?: string) => void;
  onNavigateAbout?: () => void;
  onNavigateContact?: () => void;
  onNavigate?: (page: any, sectionId?: string) => void;
  onOpenConnect?: () => void;
}


export const AboutSection: React.FC<AboutSectionProps> = ({
  onOpenInquiry,
  onNavigateAbout,
  onNavigateContact,
  onNavigate,
  onOpenConnect,
}) => {
  const { siteData } = useCms();
  const homeData = siteData?.pages?.home;
  const pageAbout = siteData?.pages?.about;
  const sectionBgColor = homeData?.aboutSectionBgColor || '#ebedf4';
  const sectionHeading = homeData?.aboutSectionHeading || 'Redefining Automation Standards';
  const aboutBoxTitle = homeData?.aboutBoxTitle || 'Engineering Excellence Since 2006';
  const aboutBoxText = homeData?.aboutBoxBody || homeData?.aboutSectionBody || 'Established in 2006, Simatech Automation provides turnkey industrial automation solutions involving PLCs, Variable Speed Drives, and SCADA architectures.';
  const aboutImageUrl = homeData?.aboutSectionImageUrl || homeData?.aboutImage || '/images/about/industrial_facility.webp';
  const aboutImageOverlayOpacity = homeData?.aboutImageOverlayOpacity !== undefined
    ? Number(homeData.aboutImageOverlayOpacity)
    : 45;
  const aboutImageOverlayColor = homeData?.aboutImageOverlayColor || '#061e24';
  const aboutCtaText = homeData?.aboutCtaText || 'Read more...';

  // Dynamic typography & styling overrides
  const headingFontFamily = homeData?.aboutHeadingFontFamily || "'Jost', sans-serif";
  const headingFontSize = homeData?.aboutHeadingFontSize;
  const headingFontWeight = homeData?.aboutHeadingFontWeight || '300';
  const headingColor = homeData?.aboutHeadingColor || '#06242a';

  const boxTitleFontFamily = homeData?.aboutBoxTitleFontFamily || "'Jost', sans-serif";
  const boxTitleFontSize = homeData?.aboutBoxTitleFontSize;
  const boxTitleFontWeight = homeData?.aboutBoxTitleFontWeight || '600';
  const boxTitleColor = homeData?.aboutBoxTitleColor || '#06242a';

  const boxBodyFontFamily = homeData?.aboutBoxBodyFontFamily || "'Jost', sans-serif";
  const boxBodyFontSize = homeData?.aboutBoxBodyFontSize;
  const boxBodyColor = homeData?.aboutBoxBodyColor || '#475569';

  // Why Automate With Us Section & Typography
  const accordionBadge = homeData?.accordionBadge || homeData?.aboutAccordionBadge || 'WHY AUTOMATE WITH US';
  const accordionHeading = homeData?.accordionHeading || homeData?.aboutAccordionHeading || 'Why Automate With Us';
  const accordionSubheading = homeData?.accordionSubheading || homeData?.aboutAccordionSubheading || '';
  const accordionHeadingFontFamily = homeData?.accordionHeadingFontFamily || homeData?.aboutAccordionHeadingFontFamily || "'Jost', sans-serif";
  const accordionHeadingFontSize = homeData?.accordionHeadingFontSize || homeData?.aboutAccordionHeadingFontSize;
  const accordionHeadingFontWeight = homeData?.accordionHeadingFontWeight || '300';
  const accordionHeadingColor = homeData?.accordionHeadingColor || homeData?.aboutAccordionHeadingColor || '#06242a';

  const accordionCardTitleFontFamily = homeData?.aboutAccordionCardTitleFontFamily || "'Jost', sans-serif";
  const accordionCardTitleFontSize = homeData?.aboutAccordionCardTitleFontSize;

  const accordionSummaryFontFamily = homeData?.accordionSummaryFontFamily || "'Jost', sans-serif";
  const accordionSummaryFontSize = homeData?.accordionSummaryFontSize;
  const accordionSummaryFontWeight = homeData?.accordionSummaryFontWeight || '300';
  const accordionSummaryColor = homeData?.accordionSummaryColor;

  const accordionBulletFontFamily = homeData?.accordionBulletFontFamily || "'Jost', sans-serif";
  const accordionBulletFontSize = homeData?.accordionBulletFontSize;
  const accordionBulletColor = homeData?.accordionBulletColor;
  const accordionBulletStyle = homeData?.accordionBulletStyle || 'cyan-dot';
  const accordionBulletIconColor = homeData?.accordionBulletIconColor || '#1b7481';
  const accordionBulletCardBg = homeData?.accordionBulletCardBg;

  // Capability Pillars (4 Cards) Typography
  const teamHeading = homeData?.teamHeading || pageAbout?.teamHeading || 'Today we are a team of dynamic engineers';
  const teamHeadingHighlight = homeData?.teamHeadingHighlight || pageAbout?.teamHeadingHighlight || 'industrial automation solution provider.';
  const teamHeadingFontFamily = homeData?.teamHeadingFontFamily || homeData?.teamFontFamily || pageAbout?.teamFontFamily || "'Jost', sans-serif";
  const teamHeadingFontSize = homeData?.teamHeadingFontSize;
  const teamHeadingFontWeight = homeData?.teamHeadingFontWeight || '300';
  const teamHeadingColor = homeData?.teamHeadingColor || pageAbout?.teamHeadingColor || '#06242a';

  const teamSubheading = homeData?.teamSubheading || pageAbout?.teamSubheading || 'In the new millennium, Simatech Automation has acquired newer technologies and quality standards. We deliver the best positive value while meeting individual customer requirements.';
  const teamSubheadingFontFamily = homeData?.teamSubheadingFontFamily;
  const teamSubheadingFontSize = homeData?.teamSubheadingFontSize;
  const teamSubheadingColor = homeData?.teamSubheadingColor || pageAbout?.teamSubheadingColor;

  const teamCardTitleFontFamily = homeData?.teamCardTitleFontFamily || "'Jost', sans-serif";
  const teamCardTitleFontSize = homeData?.teamCardTitleFontSize;
  const teamCardTitleFontWeight = homeData?.teamCardTitleFontWeight || '500';
  const teamCardTitleColor = homeData?.teamCardTitleColor || '#06242a';

  const teamCardDescFontFamily = homeData?.teamCardDescFontFamily || "'Jost', sans-serif";
  const teamCardDescFontSize = homeData?.teamCardDescFontSize;
  const teamCardDescColor = homeData?.teamCardDescColor || '#64748b';

  const renderAccordionBulletGraphic = (isDark: boolean) => {
    // Bullet cards have a clean light background for maximum readability
    const color = accordionBulletIconColor || (isDark ? '#0d6371' : '#1b7481');
    switch (accordionBulletStyle) {
      case 'teal-circle':
        return <span className="inline-block w-2.5 h-2.5 rounded-full mt-0.5 shrink-0 border-2" style={{ borderColor: color }} />;
      case 'checkmark':
        return (
          <span
            className="w-3.5 h-3.5 rounded flex items-center justify-center shrink-0 mt-0.5"
            style={{
              backgroundColor: 'rgba(27,116,129,0.12)',
              color,
            }}
          >
            <Check className="w-2.5 h-2.5" strokeWidth={2.5} />
          </span>
        );
      case 'chevron':
        return <ChevronRight className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color }} strokeWidth={2.5} />;
      case 'hyphen':
        return <span className="w-2.5 h-[2px] mt-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />;
      case 'square':
        return <span className="w-1.5 h-1.5 mt-1.5 shrink-0 rounded-[1px]" style={{ backgroundColor: color }} />;
      case 'cyan-dot':
      default:
        return (
          <span
            className="inline-block w-2 h-2 rounded-full mt-1 shrink-0"
            style={{
              backgroundColor: color,
              boxShadow: '0 0 4px rgba(27,116,129,0.3)',
            }}
          />
        );
    }
  };

  const [activePanel, setActivePanel] = useState<number>(0);

  const overviewRef = useRef<HTMLDivElement>(null);
  const accordionRef = useRef<HTMLDivElement>(null);
  const [isOverviewVisible, setIsOverviewVisible] = useState(false);
  const [isAccordionVisible, setIsAccordionVisible] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsOverviewVisible(true);
      setIsAccordionVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === overviewRef.current) {
              setIsOverviewVisible(true);
            } else if (entry.target === accordionRef.current) {
              setIsAccordionVisible(true);
            }
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    if (overviewRef.current) observer.observe(overviewRef.current);
    if (accordionRef.current) observer.observe(accordionRef.current);

    return () => observer.disconnect();
  }, []);

  const isColorDark = (hex?: string): boolean => {
    if (!hex || !hex.startsWith('#')) return false;
    const c = hex.substring(1);
    const rgb = parseInt(c.length === 3 ? c.split('').map(x => x + x).join('') : c, 16);
    if (isNaN(rgb)) return false;
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = (rgb >> 0) & 0xff;
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma < 140;
  };

  const defaultAccordionItems = [
    {
      id: 'emphasis',
      num: '01',
      title: 'Project Emphasis',
      summary:
        'We make a critical emphasis in adopting user friendliness and cost effective engineering solutions, while maintaining quality standards.',
      details: [
        'Intuitive touch-screen HMI layouts designed for seamless operator adoption.',
        'Structured fault diagnostics and real-time alarm histories for minimized MTTR.',
        'Cost-effective electrical architecture that eliminates redundancy while retaining safety.',
      ],
      bgClass: 'bg-[#ebedf4]',
      isDark: false,
      textColor: 'text-[#06242a]',
      subTextColor: 'text-slate-600',
      borderClass: 'border-slate-300/80',
    },
    {
      id: 'commitment',
      num: '02',
      title: 'Customer Commitment',
      summary:
        'Simatech Automation is committed to offer customers with a one stop customized solution in the field of industrial automation systems, PLC automation system, Variable speed electrical Drives engineering & panel fabrication. Incorporating the best industrial and engineering practices, the site-proven experience of quality-conscious of high-level dynamic engineers is committed to meet, maintain and enhance performance & safety levels in multi-discipline projects.',
      details: [
        'Single-source accountability: from initial P&ID review to final plant commissioning.',
        'Continuous performance benchmarking to guarantee optimum line throughput and uptime.',
        'Multi-discipline coordination across electrical, mechanical, and IT automation layers.',
      ],
      bgClass: 'bg-[#d2e0ea]',
      isDark: false,
      textColor: 'text-[#06242a]',
      subTextColor: 'text-slate-700',
      borderClass: 'border-slate-300/80',
    },
    {
      id: 'methodology',
      num: '03',
      title: 'Project Methodology',
      summary:
        'We handle all customer projects with utmost care by process analysis, selecting the right hardware and software, detailed engineering, testing, and seamless commissioning.',
      details: [
        'Phase 1: Rigorous process analysis, load calculations, and motor-drive sizing simulations.',
        'Phase 2: Industrial standard schematic CAD drafting and Siemens switchgear selection.',
        'Phase 3: Digital twin software simulation, Factory Acceptance Testing (FAT), and SAT.',
      ],
      bgClass: 'bg-[#b5cce0]',
      isDark: false,
      textColor: 'text-[#06242a]',
      subTextColor: 'text-slate-800',
      borderClass: 'border-slate-400/60',
    },
    {
      id: 'specialization',
      num: '04',
      title: 'Specialization',
      summary:
        'In offering a one stop customized solution to customers: Specialized in turnkey automation, custom precision control panel manufacturing, retrofitting legacy systems, and integration of Siemens S7 PLCs, SINAMICS drives, and WinCC SCADA.',
      details: [
        'Turnkey cable & wire manufacturing automation: Capstan synchronization and tension dancers.',
        'Legacy conversion: Smooth migration of obsolete Siemens S5 & 6RA24/70 DC drives to SINAMICS.',
        'Custom IP55/IP65 enclosures with LAPP internal cabling, copper busbars, and certified dissipation.',
      ],
      bgClass: 'bg-[#344c5a]',
      isDark: true,
      textColor: 'text-white',
      subTextColor: 'text-slate-200',
      borderClass: 'border-slate-700/60',
    },
  ];

  const accordionItems = useMemo(() => {
    const rawList = (homeData?.aboutAccordionItems && homeData.aboutAccordionItems.length > 0)
      ? homeData.aboutAccordionItems
      : (homeData?.accordionItems && homeData.accordionItems.length > 0)
      ? homeData.accordionItems
      : null;

    if (!rawList || rawList.length === 0) {
      return defaultAccordionItems;
    }
    const defaultPalette = [
      { bgClass: 'bg-[#ebedf4]', isDark: false, textColor: 'text-[#06242a]', subTextColor: 'text-slate-600', borderClass: 'border-slate-300/80' },
      { bgClass: 'bg-[#d2e0ea]', isDark: false, textColor: 'text-[#06242a]', subTextColor: 'text-slate-700', borderClass: 'border-slate-300/80' },
      { bgClass: 'bg-[#b5cce0]', isDark: false, textColor: 'text-[#06242a]', subTextColor: 'text-slate-800', borderClass: 'border-slate-400/60' },
      { bgClass: 'bg-[#344c5a]', isDark: true, textColor: 'text-white', subTextColor: 'text-slate-200', borderClass: 'border-slate-700/60' },
    ];
    return rawList.map((item: any, idx: number) => {
      const palette = defaultPalette[idx % defaultPalette.length];
      const isDark = item.bgColor ? isColorDark(item.bgColor) : palette.isDark;
      return {
        id: `panel-${idx}`,
        num: item.num || String(idx + 1).padStart(2, '0'),
        title: item.title,
        summary: item.summary,
        details: Array.isArray(item.details) ? item.details : typeof item.details === 'string' ? item.details.split('\n').filter(Boolean) : [],
        bgClass: palette.bgClass,
        customBg: item.bgColor,
        isDark,
        textColor: isDark ? 'text-white' : 'text-[#06242a]',
        subTextColor: isDark ? 'text-slate-200' : 'text-slate-700',
        borderClass: isDark ? 'border-slate-700/60' : palette.borderClass,
      };
    });
  }, [homeData?.aboutAccordionItems, homeData?.accordionItems]);

  const totalPanels = accordionItems.length;

  // Bounds-safe interactive panel selector
  const handleSelectPanel = (index: number) => {
    if (totalPanels === 0) return;
    const safeIndex = Math.max(0, Math.min(totalPanels - 1, index));
    setActivePanel(safeIndex);
  };

  const handleCtaClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const target = homeData?.aboutCtaTarget || 'page:about';
    if (target.startsWith('modal:inquiry')) {
      onOpenInquiry('Turnkey Process Automation Inquiry');
    } else if (target.startsWith('modal:connect')) {
      if (onOpenConnect) onOpenConnect();
    } else if (target.startsWith('page:')) {
      const pageName = target.replace('page:', '');
      if (onNavigate) {
        onNavigate(pageName);
      } else if (pageName === 'about' && onNavigateAbout) {
        onNavigateAbout();
      } else if (pageName === 'contact' && onNavigateContact) {
        onNavigateContact();
      }
    } else if (target.startsWith('#')) {
      const secId = target.replace('#', '');
      if (onNavigate) onNavigate('home', secId);
      const el = document.getElementById(secId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      if (onNavigateAbout) onNavigateAbout();
      else if (onNavigate) onNavigate('about');
    }
  };

  return (
    <section id="about" className="relative border-b border-slate-200/80" style={{ backgroundColor: sectionBgColor }}>
      
      {/* 1. EXECUTIVE OVERVIEW: BOXED LAYOUT AS EARLIER */}
      <div ref={overviewRef} className="py-16 sm:py-20 lg:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`max-w-4xl mb-10 sm:mb-12 lg:mb-14 transition-all duration-700 ease-out ${
          isOverviewVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}>
          <h2
            className="text-xl sm:text-2xl md:text-3xl lg:text-[32px] font-light tracking-tight leading-tight"
            style={{
              fontFamily: headingFontFamily,
              fontSize: headingFontSize || undefined,
              fontWeight: headingFontWeight,
              color: headingColor,
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: sectionHeading }} />
          </h2>
          <div className={`h-[2px] bg-gradient-to-r from-[#1b7481] to-[#38c9de] mt-3.5 transition-all duration-700 delay-200 ${
            isOverviewVisible ? 'w-24 opacity-100' : 'w-0 opacity-0'
          }`} />
        </div>

        {/* Visual Showcase: Boxed Container with Image, Right Column & Floating White Box */}
        <div className="relative w-full overflow-visible">
          
          <div className="relative grid grid-cols-1 lg:grid-cols-12 min-h-[360px] sm:min-h-[440px] lg:min-h-[540px]">
            
            {/* Left & Center: Corporate Image Canvas */}
            <div className={`lg:col-span-10 relative overflow-hidden h-[320px] sm:h-[420px] lg:h-[540px] rounded-2xl lg:rounded-l-3xl lg:rounded-r-none transition-all duration-700 ease-out group/img ${
              isOverviewVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-[0.98]'
            }`}>
              <img
                src={aboutImageUrl}
                alt="Simatech Automation Industrial Engineering & Manufacturing Facility"
                loading="lazy"
                decoding="async"
                width={960}
                height={640}
                className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover/img:scale-105"
                onError={(e) => {
                  const img = e.currentTarget;
                  if (!img.src.includes('industrial_facility.webp')) {
                    img.src = '/images/about/industrial_facility.webp';
                  }
                }}
              />
              
              {/* Background image overlay with tint & opacity */}
              <div
                className="absolute inset-0 pointer-events-none transition-opacity duration-500"
                style={{
                  backgroundColor: aboutImageOverlayColor,
                  opacity: aboutImageOverlayOpacity / 100,
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />

              {/* Light sheen glare sweep across image on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover/img:translate-x-full transition-transform duration-1000 ease-out pointer-events-none" />
            </div>

            {/* Right Structural Dark Column */}
            <div className={`hidden lg:block lg:col-span-2 bg-[#1c2e3d] h-full rounded-r-3xl transition-all duration-700 delay-150 ${
              isOverviewVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
            }`} />

            {/* Overlapping Floating White Box with Onload Reveal and Hover Lift */}
            <div className={`relative lg:absolute lg:right-6 xl:right-10 lg:bottom-10 z-20 -mt-16 sm:-mt-20 lg:mt-0 mx-4 sm:mx-6 lg:mx-0 max-w-md bg-white rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.12)] p-7 sm:p-9 md:p-10 border border-slate-100/90 transition-all duration-700 delay-300 hover:-translate-y-2.5 hover:shadow-[0_30px_70px_rgba(27,116,129,0.22)] hover:border-[#1b7481]/30 group/box ${
              isOverviewVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}>
              
              {/* Subtle top cyan line highlight */}
              <div className="absolute top-0 left-8 right-8 h-[2px] bg-gradient-to-r from-transparent via-[#1b7481] to-transparent opacity-0 group-hover/box:opacity-100 transition-opacity duration-500" />

              <h3
                className="text-xl sm:text-2xl font-semibold tracking-tight mb-4 group-hover/box:text-[#1b7481] transition-colors"
                style={{
                  fontFamily: boxTitleFontFamily,
                  fontSize: boxTitleFontSize || undefined,
                  fontWeight: boxTitleFontWeight,
                  color: boxTitleColor,
                }}
              >
                {aboutBoxTitle}
              </h3>

              <p
                className="text-xs sm:text-sm font-light leading-relaxed mb-6"
                style={{
                  fontFamily: boxBodyFontFamily,
                  fontSize: boxBodyFontSize || undefined,
                  color: boxBodyColor,
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: aboutBoxText }} />
              </p>

              <div>
                <button
                  type="button"
                  onClick={handleCtaClick}
                  className="group/btn inline-flex items-center gap-2 text-sm font-semibold text-[#06242a] hover:text-[#1b7481] transition-colors cursor-pointer border-0 p-0 bg-transparent"
                >
                  <span className="relative">
                    {aboutCtaText}
                    <span className="absolute left-0 -bottom-0.5 w-0 group-hover/btn:w-full h-[1.5px] bg-[#1b7481] transition-all duration-300" />
                  </span>
                  <ArrowUpRight className="w-4 h-4 transform group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform text-[#06242a] group-hover/btn:text-[#1b7481]" />
                </button>
              </div>

            </div>

          </div>

        </div>

      </div>

      {/* ========================================================================= */}
      {/* 2. WHY AUTOMATE WITH US: INTERACTIVE RESPONSIVE ACCORDION SHOWCASE        */}
      {/* ========================================================================= */}
      <div ref={accordionRef} className={`relative bg-white border-t border-slate-200/80 pt-10 sm:pt-14 lg:pt-16 pb-12 sm:pb-16 lg:pb-20 transition-all duration-700 ease-out ${
        isAccordionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header Row — Symmetric vertical spacing */}
          <div className="mb-10 sm:mb-14 lg:mb-16">
            <div>
              <h2
                className="text-xl sm:text-2xl md:text-3xl lg:text-[32px] tracking-tight leading-tight"
                style={{
                  fontFamily: accordionHeadingFontFamily,
                  fontSize: accordionHeadingFontSize || undefined,
                  fontWeight: accordionHeadingFontWeight,
                  color: accordionHeadingColor,
                }}
              >
                {accordionHeading.includes('With Us') ? (
                  <>
                    {accordionHeading.replace('With Us', '')}{' '}
                    <span className="font-normal text-transparent bg-clip-text bg-gradient-to-r from-[#11505a] via-[#1b7481] to-[#38c9de]">
                      With Us
                    </span>
                  </>
                ) : (
                  <span dangerouslySetInnerHTML={{ __html: accordionHeading }} />
                )}
              </h2>
              <div className="h-[1px] w-16 bg-gradient-to-r from-[#1b7481] to-[#38c9de] mt-3.5" />
              {accordionSubheading && (
                <p className="mt-2.5 text-xs sm:text-sm text-slate-600 font-light max-w-2xl leading-relaxed">
                  {accordionSubheading}
                </p>
              )}
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* MOBILE VIEW (< md): Touch Tabs + Elevated Panel Card           */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <div className="block md:hidden">
            {/* Horizontal Touch Scroll Tab Bar */}
            <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-4 scrollbar-none">
              {accordionItems.map((item, idx) => {
                const isActive = activePanel === idx;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleSelectPanel(idx)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all flex items-center gap-2 shrink-0 border cursor-pointer ${
                      isActive
                        ? 'bg-[#0f3b43] text-white border-[#1b7481] shadow-sm font-semibold'
                        : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
                    }`}
                  >
                    <span className={`font-mono text-[11px] ${isActive ? 'text-[#38c9de]' : 'text-slate-400'}`}>
                      {item.num}
                    </span>
                    <span>{item.title}</span>
                  </button>
                );
              })}
            </div>

            {/* Mobile Active Panel Card */}
            {accordionItems[activePanel] && (() => {
              const item = accordionItems[activePanel];
              return (
                <div
                  style={{ backgroundColor: item.customBg || undefined }}
                  className={`rounded-2xl p-5 sm:p-6 border shadow-sm ${item.bgClass} ${item.borderClass} transition-all duration-300`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-3xl font-light font-['Jost',sans-serif] ${item.textColor}`}>
                      {item.num}
                    </span>
                    <span className="text-[10px] font-mono tracking-widest uppercase px-2.5 py-1 rounded-full bg-black/10">
                      Pillar 0{activePanel + 1}
                    </span>
                  </div>

                  <h3 className={`text-xl font-bold font-['Jost',sans-serif] tracking-tight leading-snug mb-3 ${item.textColor}`}>
                    {item.title}
                  </h3>

                  <p
                    className={`text-xs sm:text-sm leading-relaxed mb-4 ${item.subTextColor}`}
                    style={{
                      fontFamily: accordionSummaryFontFamily,
                      fontSize: accordionSummaryFontSize || undefined,
                      fontWeight: accordionSummaryFontWeight,
                      color: accordionSummaryColor || undefined,
                    }}
                  >
                    {item.summary}
                  </p>

                  {/* Bullet Points */}
                  <div className="space-y-2 mb-5">
                    {item.details.map((bullet: string, bIdx: number) => (
                      <div
                        key={bIdx}
                        style={{ backgroundColor: accordionBulletCardBg || '#ffffff' }}
                        className="p-3 rounded-xl border border-slate-200/90 shadow-xs text-xs leading-relaxed"
                      >
                        <div className="flex items-start gap-2.5">
                          {renderAccordionBulletGraphic(item.isDark)}
                          <span
                            style={{
                              fontFamily: accordionBulletFontFamily,
                              fontSize: accordionBulletFontSize || undefined,
                              color: accordionBulletColor || '#0f172a',
                            }}
                          >
                            {bullet}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Mobile Prev / Next Buttons */}
                  <div className="flex items-center justify-between pt-3 border-t border-black/10">
                    <button
                      type="button"
                      onClick={() => handleSelectPanel(activePanel - 1)}
                      disabled={activePanel === 0}
                      className="px-3.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all bg-white/70 hover:bg-white text-slate-800 disabled:opacity-30 disabled:pointer-events-none border border-slate-300 shadow-xs cursor-pointer"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" /> Previous
                    </button>
                    <span className="text-xs font-mono text-slate-500">
                      {activePanel + 1} of {totalPanels}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleSelectPanel(activePanel + 1)}
                      disabled={activePanel === totalPanels - 1}
                      className="px-3.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all bg-[#11505a] hover:bg-[#0c3940] text-white disabled:opacity-30 disabled:pointer-events-none shadow-xs cursor-pointer"
                    >
                      Next <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* DESKTOP VIEW (md+): Smooth Horizontal Expanding Accordion      */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <div className="hidden md:flex relative w-full h-[480px] lg:h-[520px] rounded-3xl overflow-hidden p-2 sm:p-2.5 gap-2.5 sm:gap-3 bg-slate-50/80 backdrop-blur-md border border-slate-200 shadow-[0_20px_50px_rgba(15,23,42,0.06)]">
            
            {/* Left Navigation Arrow */}
            <button
              type="button"
              onClick={() => handleSelectPanel(activePanel - 1)}
              disabled={activePanel === 0}
              aria-label="Previous panel"
              className={`absolute left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-md ${
                activePanel === 0
                  ? 'opacity-0 pointer-events-none bg-[#475569]/60 text-white'
                  : 'bg-[#1e293b]/85 hover:bg-[#0f172a] text-white hover:scale-105 active:scale-95 cursor-pointer backdrop-blur-xs'
              }`}
            >
              <ChevronLeft className="w-5 h-5 -translate-x-0.5" />
            </button>

            {/* Right Navigation Arrow */}
            <button
              type="button"
              onClick={() => handleSelectPanel(activePanel + 1)}
              disabled={activePanel === totalPanels - 1}
              aria-label="Next panel"
              className={`absolute right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-md ${
                activePanel === totalPanels - 1
                  ? 'opacity-0 pointer-events-none bg-[#1e293b]/60 text-white'
                  : 'bg-[#1e293b]/85 hover:bg-[#0f172a] text-white hover:scale-105 active:scale-95 cursor-pointer backdrop-blur-xs'
              }`}
            >
              <ChevronRight className="w-5 h-5 translate-x-0.5" />
            </button>

            {/* Panels with Smooth Left-to-Right Flex & Transform Animation */}
            {accordionItems.map((item, idx) => {
              const isActive = activePanel === idx;

              return (
                <div
                  key={item.id}
                  onClick={() => {
                    if (!isActive) handleSelectPanel(idx);
                  }}
                  style={{
                    flex: isActive ? `${Math.max(5, totalPanels + 2)} 1 0%` : '1 1 0%',
                    backgroundColor: item.customBg || undefined,
                  }}
                  className={`relative rounded-2xl lg:rounded-[26px] overflow-hidden transition-[flex,box-shadow,transform,filter] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col ${
                    item.bgClass
                  } ${item.borderClass} border ${
                    isActive
                      ? 'shadow-[0_16px_40px_rgba(0,0,0,0.12)] cursor-default ring-1 ring-[#1b7481]/30'
                      : 'hover:brightness-95 hover:scale-[1.015] hover:shadow-lg cursor-pointer group/strip'
                  }`}
                >
                  {/* 1. ACTIVE EXPANDED PANEL VIEW */}
                  <div
                    className={`absolute inset-0 p-6 lg:p-10 pl-14 lg:pl-16 pr-14 lg:pr-16 flex flex-col justify-between transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                      isActive
                        ? 'opacity-100 translate-x-0 pointer-events-auto z-10'
                        : 'opacity-0 -translate-x-6 pointer-events-none z-0'
                    }`}
                  >
                    <div className="w-full h-full flex flex-col justify-between">
                      {/* Top Row: Big Number */}
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-4xl lg:text-5xl font-light tracking-tight font-['Jost',sans-serif] ${item.textColor}`}
                        >
                          {item.num}
                        </span>
                        <span className={`text-[10px] font-mono uppercase px-3 py-1 rounded-full border ${
                          item.isDark ? 'border-white/20 text-white/80 bg-white/5' : 'border-slate-300 text-slate-600 bg-black/5'
                        }`}>
                          Pillar 0{idx + 1}
                        </span>
                      </div>

                      {/* Middle Content */}
                      <div className="my-auto py-2">
                        <h3
                          className={`text-2xl lg:text-[30px] font-bold font-['Jost',sans-serif] tracking-tight leading-tight mb-3 ${item.textColor}`}
                        >
                          {item.title}
                        </h3>

                        <p
                          className={`text-xs lg:text-sm leading-relaxed mb-5 max-w-2xl ${item.subTextColor}`}
                          style={{
                            fontFamily: accordionSummaryFontFamily,
                            fontSize: accordionSummaryFontSize || undefined,
                            fontWeight: accordionSummaryFontWeight,
                            color: accordionSummaryColor || undefined,
                          }}
                        >
                          {item.summary}
                        </p>

                        {/* Detail Bullets Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 lg:gap-3">
                          {item.details.map((bullet: string, bIdx: number) => (
                            <div
                              key={bIdx}
                              style={{ backgroundColor: accordionBulletCardBg || '#ffffff' }}
                              className="p-3.5 rounded-xl border border-slate-200/90 shadow-xs transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-default text-xs leading-relaxed"
                            >
                              <div className="flex items-start gap-2.5">
                                {renderAccordionBulletGraphic(item.isDark)}
                                <span
                                  className="line-clamp-3 leading-relaxed"
                                  style={{
                                    fontFamily: accordionBulletFontFamily,
                                    fontSize: accordionBulletFontSize || undefined,
                                    color: accordionBulletColor || '#0f172a',
                                  }}
                                >
                                  {bullet}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Bottom Footer Indicator */}
                      <div className="flex items-center justify-between text-[11px] font-mono opacity-70">
                        <span className={item.textColor}>Strategic Pillar {idx + 1} of {totalPanels}</span>
                        <div className="flex items-center gap-1.5">
                          {accordionItems.map((_, pIdx) => (
                            <div
                              key={pIdx}
                              className={`w-1.5 h-1.5 rounded-full transition-all ${
                                pIdx === idx
                                  ? (item.isDark ? 'bg-[#38c9de] w-4' : 'bg-[#1b7481] w-4')
                                  : (item.isDark ? 'bg-white/30' : 'bg-slate-400/40')
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 2. COLLAPSED NARROW STRIP VIEW */}
                  <div
                    className={`absolute inset-0 flex flex-col items-center justify-between py-6 select-none transition-all duration-400 ease-out ${
                      isActive
                        ? 'opacity-0 pointer-events-none z-0'
                        : 'opacity-100 pointer-events-auto z-10'
                    }`}
                  >
                    {/* Top Number */}
                    <span
                      className={`text-2xl lg:text-3xl font-light font-['Jost',sans-serif] ${item.textColor}`}
                    >
                      {item.num}
                    </span>

                    {/* Vertical Rotated Title */}
                    <div className="flex-1 flex items-center justify-center my-4">
                      <span
                        className={`[writing-mode:vertical-rl] rotate-180 text-xs font-semibold tracking-wider uppercase whitespace-nowrap opacity-85 ${item.textColor}`}
                      >
                        {item.title}
                      </span>
                    </div>

                    {/* Bottom Accent Indicator */}
                    <div
                      className={`w-2 h-2 rounded-full ${
                        item.isDark ? 'bg-white/40' : 'bg-slate-400/50'
                      }`}
                    />
                  </div>
                </div>
              );
            })}

          </div>

        </div>
      </div>

      {/* ========================================================================= */}
      {/* 3. TEAM OF DYNAMIC ENGINEERS & 4 CAPABILITY PILLARS                        */}
      {/* ========================================================================= */}
      <div className="py-16 sm:py-20 lg:py-24 bg-[#f1f5f9] border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-4xl mx-auto mb-10 sm:mb-12 lg:mb-14">
            <h2
              className="text-lg sm:text-xl md:text-2xl lg:text-[27px] tracking-tight leading-[1.25] mb-3.5 sm:mb-4"
              style={{
                fontFamily: teamHeadingFontFamily,
                fontSize: teamHeadingFontSize || undefined,
                fontWeight: teamHeadingFontWeight,
                color: teamHeadingColor,
              }}
            >
              {teamHeading}{' '}
              <br className="hidden sm:inline" />
              <span
                className="text-transparent bg-clip-text"
                style={{
                  fontWeight: teamHeadingFontWeight,
                  backgroundImage: `linear-gradient(to right, ${homeData?.teamHighlightFromColor || pageAbout?.teamHighlightFromColor || '#11505a'}, ${homeData?.teamHighlightViaColor || pageAbout?.teamHighlightViaColor || '#1b7481'}, ${homeData?.teamHighlightToColor || pageAbout?.teamHighlightToColor || '#38c9de'})`,
                }}
              >
                {teamHeadingHighlight}
              </span>
            </h2>
            <div
              className="h-[1px] w-16 mx-auto mb-5"
              style={{
                background: `linear-gradient(to right, ${homeData?.teamHighlightViaColor || pageAbout?.teamHighlightViaColor || '#1b7481'}, ${homeData?.teamHighlightToColor || pageAbout?.teamHighlightToColor || '#38c9de'})`,
              }}
            />

            <div
              className="text-base sm:text-lg text-slate-600 font-light leading-relaxed max-w-3xl mx-auto [&_strong]:font-semibold [&_em]:italic [&_a]:text-[#1b7481]"
              style={{
                fontFamily: teamSubheadingFontFamily || undefined,
                fontSize: teamSubheadingFontSize || undefined,
                color: teamSubheadingColor || undefined,
              }}
              dangerouslySetInnerHTML={{
                __html: teamSubheading,
              }}
            />
          </div>

          {(() => {
            const rawCards = (homeData?.teamCards && homeData.teamCards.length > 0)
              ? homeData.teamCards
              : [
                  {
                    icon: 'Cpu',
                    title: 'Siemens System House',
                    desc: 'Authorized supply and engineering of SIMATIC S7 PLCs & SINAMICS drives.',
                  },
                  {
                    icon: 'Globe2',
                    title: '14+ Export Countries',
                    desc: 'On-site field commissioning across Middle East, Africa, Asia, and Americas.',
                  },
                  {
                    icon: 'Zap',
                    title: 'Legacy Retrofits',
                    desc: 'Seamless migration of obsolete S5 PLCs & 6RA24/70 DC drives to modern SINAMICS.',
                  },
                ];
            const cardsToDisplay = rawCards.filter((c: any) => !c.title?.toLowerCase().includes('cpri'));
            const gridClass = cardsToDisplay.length === 3
              ? 'grid-cols-1 md:grid-cols-3 max-w-5xl'
              : cardsToDisplay.length === 2
              ? 'grid-cols-1 sm:grid-cols-2 max-w-3xl'
              : cardsToDisplay.length === 1
              ? 'grid-cols-1 max-w-md'
              : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';

            return (
              <div className={`grid ${gridClass} mx-auto gap-6 sm:gap-8 justify-center`}>
                {cardsToDisplay.map((card: any, cIdx: number) => {
                  const renderCardIcon = (iconName: string) => {
                    switch (iconName) {
                      case 'Cpu': return <Cpu className="w-5 h-5" />;
                      case 'Globe2': return <Globe2 className="w-5 h-5" />;
                      case 'Zap': return <Zap className="w-5 h-5" />;
                      case 'ShieldCheck': return <ShieldCheck className="w-5 h-5" />;
                      case 'Compass': return <Compass className="w-5 h-5" />;
                      case 'Layers':
                      default: return <Layers className="w-5 h-5" />;
                    }
                  };

                  return (
                    <div key={card.id || cIdx} className="p-6 rounded-2xl bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-all group">
                      <div className="w-11 h-11 rounded-xl bg-[#1b7481]/10 text-[#1b7481] group-hover:bg-[#1b7481] group-hover:text-white flex items-center justify-center mb-4 transition-all duration-300">
                        {renderCardIcon(card.icon)}
                      </div>
                      <h3
                        className="text-base mb-1.5"
                        style={{
                          fontFamily: teamCardTitleFontFamily,
                          fontSize: teamCardTitleFontSize || undefined,
                          fontWeight: teamCardTitleFontWeight,
                          color: teamCardTitleColor,
                        }}
                      >
                        {card.title}
                      </h3>
                      <p
                        className="text-xs leading-relaxed"
                        style={{
                          fontFamily: teamCardDescFontFamily,
                          fontSize: teamCardDescFontSize || undefined,
                          color: teamCardDescColor,
                        }}
                      >
                        {card.desc || card.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            );
          })()}

        </div>
      </div>

    </section>
  );
};
